/**
 * @file iframe 桥接hook
 */
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import { IframeBridge } from '@baidu/code-live-utils';

interface IConnectionState {
    isConnected: boolean;
    error: string | null;
    connectedAt: number | null;
}

export function useIframeBridge(
    setupCommunication: (bridge: IframeBridge) => void
) {
    const bridgeRef = useRef<IframeBridge | null>(null);
    const [connectionState, setConnectionState] = useState<IConnectionState>(null);
    
    const connectToIframe = useCallback(async (iframeWindow: Window) => {
        // 提前初始化Bridge + 提前设置API
        const bridge = new IframeBridge({
            targetOrigin: '*',
            enableLogging: true,
        });
        setupCommunication(bridge);
        bridgeRef.current = bridge;
        
        setConnectionState(null);
        try {
            await bridge.connect(iframeWindow);
            setConnectionState({
                isConnected: true,
                error: null,
                connectedAt: Date.now()
            });
            return bridge;
        } catch (error) {
            setConnectionState({
                isConnected: false,
                error: error.message,
                connectedAt: null
            });
            throw error;
        }
    }, [setupCommunication]);
    
    useEffect(() => {
        // 组件卸载时断开连接
        return () => {
            const bridge = bridgeRef.current;
            if (bridge) {
                bridge.destroy();
                bridgeRef.current = null;
                setConnectionState(null);
            }
        };
    }, []);
    
    return {
        bridge: bridgeRef.current,
        connectionState,
        connectToIframe,
    };
}
